import DownloadIcon from "@mui/icons-material/Download"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Collapse,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useMatch, useNavigate, useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import ApplicationsFilterPanelFrame from "../../components/fitlerPanel/applicationsFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import UserSuggestionAutocomplete from "../../components/inputs/controlled/userSuggestionAutocomplete"
import AppPaper from "../../components/surfaces/appPaper"
import ApplicationTable from "../../components/tables/applicationTable/applicationTable"
import {
    DnoAllResovedStauses,
    DnoApplicationTypes,
    DnoEscalationStatuses,
} from "../../constants/enums"
import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    SLIDE_IN_TIMEOUT,
    STATUS,
} from "../../constants/generalConstants"
import {
    TT_HELP_ALL_NOT_ESCALATED,
    TT_HELP_ESCALATED,
} from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import useDownloadJson from "../../hooks/useDownloadJson"
import { useGetState } from "../../hooks/useGetState"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import {
    DNO_APPLICATIONS_ESCALATIONS,
    DNO_APPLICATIONS_NOTIFICATIONS,
} from "../../router/routes"
import { User } from "../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import useGetDnoApplications from "../../services/middleware/hooks/dnoInterface/protected/useGetDnoApplications"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

/**
 * Lists applications for DNOs and enables filtering.
 */
const ListApplications = (): ReactElement => {
    // Api hooks
    const isEscalations = !!useMatch(DNO_APPLICATIONS_ESCALATIONS)
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        data,
        isError,
        isValidating,
        isLoading,
        downloadData,
        isFinalPage,
        page,
        setPage,
    } = useGetDnoApplications(searchParams, isEscalations)

    // Support hooks
    const { downloadJson } = useDownloadJson()
    const showFilters = useGetState(getShowDnoApplicationFilters)
    const navigate = useNavigate()

    // Filter and sort hooks
    const {
        applicationId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        status,
        setOrderBy,
        assignedTo,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, true)
    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localApplicationId, setLocalApplicationId] =
        useState<string>(applicationId)

    const onSelectUserToFilter = (newValue: User | null): void => {
        setFilterFromTextfield(newValue?.username || "", ASSIGNED_TO)
    }

    const theme = useTheme()

    return (
        <Grid container spacing={GRID_SPACING}>
            <ApplicationsFilterPanelFrame
                applicationId={applicationId}
                isValidating={isValidating}
                all={!isEscalations}
                heading={isEscalations ? "Escalated" : "Resolved"}
                applications={
                    isEscalations
                        ? "Applications Awaiting Your Review"
                        : "Applications"
                }
                tooltip={
                    isEscalations
                        ? TT_HELP_ESCALATED
                        : TT_HELP_ALL_NOT_ESCALATED
                }
                colour={
                    isEscalations
                        ? theme.palette.primary.main
                        : theme.palette.success.main
                }
                isDnoMode
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={2.5} md={7} sm={12} xs={12}>
                                <ToggleButtonGroup
                                    color="primary"
                                    fullWidth
                                    value={
                                        isEscalations
                                            ? DnoApplicationTypes.escalationsIncCx
                                            : DnoApplicationTypes.allApprovedIncCx
                                    }
                                    exclusive
                                    onChange={(_e, v) => {
                                        const to =
                                            v ===
                                            DnoApplicationTypes.escalationsIncCx
                                                ? DNO_APPLICATIONS_ESCALATIONS
                                                : DNO_APPLICATIONS_NOTIFICATIONS
                                        searchParams.delete(STATUS)
                                        navigate(
                                            `${to}${
                                                searchParams.toString()
                                                    ? `?${searchParams.toString()}`
                                                    : ""
                                            }`
                                        )
                                    }}
                                >
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.escalationsIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        Escalations
                                    </ToggleButton>
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.allApprovedIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        All
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid xl={2.5} md={5} sm={6} xs={12}>
                                <TextField
                                    label="Application ID"
                                    value={localApplicationId}
                                    placeholder="Full application ID required..."
                                    onChange={(event) => {
                                        setLocalApplicationId(
                                            event.target.value
                                        )
                                        setFilterFromTextfield(
                                            event.target.value,
                                            APPLICATION_ID_KW
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={2.5} md={5} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_FROM_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={2.5} md={5} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_TO_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={2.5} md={5} sm={6} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={Object.values(
                                        isEscalations
                                            ? DnoEscalationStatuses
                                            : DnoAllResovedStauses
                                    )}
                                    label="Status"
                                    setValue={(
                                        e_: SyntheticEvent<Element, Event>,
                                        nv: string | number | null
                                    ) =>
                                        setFilterFromTextfield(
                                            nv as string,
                                            STATUS
                                        )
                                    }
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={2.5} md={5} sm={6} xs={12}>
                                <UserSuggestionAutocomplete
                                    onSelectedUserChange={onSelectUserToFilter}
                                    userId={assignedTo}
                                    filter
                                />
                            </Grid>
                            <Grid xs>
                                <AppButton
                                    variant="contained"
                                    endIcon={<DownloadIcon />}
                                    disabled={
                                        isLoading || isError || !downloadData
                                    }
                                    onClick={() =>
                                        downloadData &&
                                        downloadJson(downloadData, "results")
                                    }
                                >
                                    Download
                                </AppButton>
                            </Grid>
                        </Grid>
                    </Collapse>

                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            <ApplicationTable
                                searchResults={data}
                                orderBy={orderBy}
                                setOrderBy={setOrderBy}
                                orderDirection={orderDirection}
                                isLoading={isLoading}
                                isEscalations={isEscalations}
                            />
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {/* final page */}
                                    {!isFinalPage && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: "20%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default ListApplications
